import { useState } from "react";
import { Search, Circle } from "lucide-react";
import type { Modem } from "../types/types";
import defaultLogo from "../../pc_logo.ico";
interface ModemSidebarProps {
  modems: Modem[];
  selectedModemId?: string;
  onModemSelect: (modemId: string) => void;
}

export function ModemSidebar({
  modems,
  selectedModemId,
  onModemSelect,
}: ModemSidebarProps) {
  const logoUrl = sessionStorage.getItem("previewAppLogo");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredModems = modems.filter((modem) => {
    const matchesSearch =
      modem.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      modem.unit_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || modem.operational_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "online":
        return "text-green-500";
      case "alarm":
        return "text-red-500";
      case "offline":
        return "text-gray-500";
      case "invalid-data":
        return "text-orange-500";
      default:
        return "text-amber-500";
    }
  };

  return (
    <div className="w-80 bg-slate-900 border-r border-slate-800 flex flex-col h-full">
      <div className="h-20 min-h-20 max-h-20 border-b border-slate-800 flex items-center justify-center overflow-hidden">
        <img
          src={logoUrl || defaultLogo}
          alt="Company Logo"
          className="h-auto w-[98%] max-h-[70px]"
        />
      </div>

      <div className="p-4 border-b border-slate-800">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            placeholder="Search units..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700  text-white text-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setStatusFilter("all")}
            className={`flex-1 px-3 py-1.5 text-xs  transition-colors ${
              statusFilter === "all"
                ? "bg-blue-600 text-white"
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            All
          </button>
          <button
            onClick={() => setStatusFilter("online")}
            className={`flex-1 px-3 py-1.5 text-xs  transition-colors ${
              statusFilter === "online"
                ? "bg-green-600 text-white"
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            Running
          </button>
          <button
            onClick={() => setStatusFilter("alarm")}
            className={`flex-1 px-3 py-1.5 text-xs  transition-colors ${
              statusFilter === "alarm"
                ? "bg-red-600 text-white"
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            Alarm
          </button>
          <button
            onClick={() => setStatusFilter("offline")}
            className={`flex-1 px-3 py-1.5 text-xs  transition-colors ${
              statusFilter === "offline"
                ? "bg-gray-600 text-white"
                : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            Missed Com
          </button>
        </div>

        <div className="text-xs text-slate-400 mt-3">
          Showing {filteredModems.length} of {modems.length} units
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filteredModems.map((modem) => (
          <button
            key={modem.id}
            onClick={() => onModemSelect(modem.id)}
            className={`w-full px-4 py-3 text-left border-b border-slate-800 transition-colors ${
              selectedModemId === modem.id
                ? "bg-slate-800 border-l-4 border-l-blue-500"
                : "hover:bg-slate-850"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">
                  {modem.name}
                </div>
                <div className="text-xs text-slate-400 truncate">
                  {modem.unit_name}
                </div>
              </div>
              <Circle
                className={`w-3 h-3 fill-current ${getStatusColor(
                  modem.operational_status
                )}`}
              />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}